import React, { type FC, type PropsWithChildren, type ReactNode } from 'react'
import { Box, Stack, type SvgIconProps, type SxProps, type Theme, Typography } from '@mui/material'

import type { ReadonlyRecord } from '../../../utils'

import { extractDataTestId } from '../utils'
import { AlertTriangleOutlineIcon, ResourcesOutlineIcon } from './Branding'

type Variant = 'empty' | 'error'

const defaultIcons: ReadonlyRecord<Variant, FC<SvgIconProps>> = { empty: ResourcesOutlineIcon, error: AlertTriangleOutlineIcon }
const colors: ReadonlyRecord<Variant, string> = { empty: 'grey.600', error: 'error.main' }
const titleColors: ReadonlyRecord<Variant, string> = { empty: 'grey.900', error: 'error.main' }

const wrapperStyle: SxProps<Theme> = { alignItems: 'center', justifyContent: 'center', gap: 3, py: 10, px: 6, textAlign: 'center' }
const messageStyle: SxProps<Theme> = { alignItems: 'center', gap: 3 }
const descriptionStyle: SxProps<Theme> = { maxWidth: 360, color: 'grey.600' }
const actionsStyle: SxProps<Theme> = { display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 2 }

type Props =
    & Readonly<{
        variant?: Variant
        icon?: FC<SvgIconProps>
        title: ReactNode
        actions?: ReactNode
    }>
    & PropsWithChildren

/** A centred block that takes the place of content that is empty or failed to load */
export const StateBlock: FC<Props> = ({ variant = 'empty', icon: Icon = defaultIcons[variant], title, actions, children, ...props }) => (
    <Stack sx={[wrapperStyle, { color: colors[variant] }]} data-testid={extractDataTestId(props) || `state-block-${variant}`}>
        <Icon fontSize="medium" />
        <Stack sx={messageStyle} role={variant === 'error' ? 'alert' : undefined}>
            <Typography variant="body2" component="p" fontWeight={600} color={titleColors[variant]}>
                {title}
            </Typography>
            {children && (
                <Typography variant="body2" component="div" sx={descriptionStyle}>
                    {children}
                </Typography>
            )}
        </Stack>
        {actions && <Box sx={actionsStyle}>{actions}</Box>}
    </Stack>
)
